import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import TokenPurchasePanel from './TokenPurchasePanel';
import ProfileDropdown from './ProfileDropdown';
import TokenIcon from './TokenIcon';
import { useAuth } from '../features/auth';
import { useCheckout } from '../features/checkout';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';

const AppHeader = () => {
  const { user, logout, requireLogin, resendVerification } = useAuth();
  const { dismissTokenCheckout, optimisticTokensSpent, tokenSpendEvent, tokenGainEvent } = useCheckout();
  const { lockerGainEvent } = useLocker();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const lockerActive = location.pathname === '/locker';
  const handleLogout = useCallback(() => {
    if (typeof logout === 'function') {
      logout().catch(() => {});
    }
  }, [logout]);
  const userTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
  const displayTokens = Math.max(userTokens - optimisticTokensSpent, 0);
  const lockerItems = Array.isArray(user?.locker)
    ? user.locker
    : Array.isArray(user?.history)
    ? user.history
    : [];
  const lockerCount = lockerItems.length;
  const maxLockerSlots = Number.isFinite(user?.maxLockerSlots) ? user.maxLockerSlots : 8;
  const displayName = user?.displayName || user?.username || user?.email || 'PonPon Fan';
  const canOpenLocker = Boolean(user);
  const handleLockerClick = useCallback(() => {
    if (!user) {
      requireLogin?.();
      return;
    }
    // CHANGED: block navigation with toast if email is not verified
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      // CHANGED: match legacy wording and include Resend link
      showToast({
        message: (
          <>
            Verify your email to unlock all features.&nbsp;
            <button
              type="button"
              onClick={() => { try { resendVerification?.(); } catch (_) {} }}
              className="font-semibold underline underline-offset-2 hover:opacity-80"
            >
              Resend.
            </button>
          </>
        ),
        tone: 'info',
      });
      return;
    }
    navigate('/locker');
  }, [user, navigate, requireLogin, showToast, resendVerification]);
  const [showTokenPanel, setShowTokenPanel] = useState(false);
  const [tokenDelta, setTokenDelta] = useState(null);
  const tokenDeltaRef = useRef(null);
  const [lockerFlash, setLockerFlash] = useState(null);
  const tokenPanelRef = useRef(null);

  const previousUserIdRef = useRef(Symbol('init'));

  useEffect(() => {
    const nextUserId = user?.id ?? null;
    if (previousUserIdRef.current === nextUserId) {
      return;
    }
    previousUserIdRef.current = nextUserId;
    setShowTokenPanel(false);
    dismissTokenCheckout();
  }, [user, dismissTokenCheckout]);

  // Panel logic moved inside TokenPurchasePanel. Header only opens/closes it.

  const handlePanelClose = useCallback(() => {
    setShowTokenPanel(false);
    dismissTokenCheckout();
  }, [dismissTokenCheckout]);

  useEffect(() => {
    if (!showTokenPanel) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (!tokenPanelRef.current) {
        return;
      }
      if (!tokenPanelRef.current.contains(event.target)) {
        handlePanelClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showTokenPanel, handlePanelClose]);

  const handleProfileMenuOpenChange = useCallback(
    (isOpen) => {
      if (isOpen && showTokenPanel) {
        handlePanelClose();
      }
    },
    [showTokenPanel, handlePanelClose]
  );

  useEffect(() => {
    if (!tokenSpendEvent || !Number.isFinite(Number(tokenSpendEvent.amount))) {
      return;
    }
    const payload = {
      amount: Number(tokenSpendEvent.amount),
      id: tokenSpendEvent.id ?? Date.now(),
      kind: 'spend',
    };
    setTokenDelta(payload);
    tokenDeltaRef.current = payload.id;
    const timeout = setTimeout(() => {
      if (tokenDeltaRef.current === payload.id) {
        setTokenDelta(null);
        tokenDeltaRef.current = null;
      }
    }, 900);
    return () => clearTimeout(timeout);
  }, [tokenSpendEvent]);

  useEffect(() => {
    if (!tokenGainEvent || !Number.isFinite(Number(tokenGainEvent.amount))) {
      return;
    }
    const payload = {
      amount: Number(tokenGainEvent.amount),
      id: tokenGainEvent.id ?? Date.now(),
      kind: 'gain',
    };
    setTokenDelta(payload);
    tokenDeltaRef.current = payload.id;
    const timeout = setTimeout(() => {
      if (tokenDeltaRef.current === payload.id) {
        setTokenDelta(null);
        tokenDeltaRef.current = null;
      }
    }, 900);
    return () => clearTimeout(timeout);
  }, [tokenGainEvent]);

  useEffect(() => {
    if (!lockerGainEvent || !Number.isFinite(Number(lockerGainEvent.amount))) {
      return;
    }
    setLockerFlash({
      amount: Number(lockerGainEvent.amount),
      id: lockerGainEvent.id ?? Date.now(),
    });
    const timeout = setTimeout(() => setLockerFlash(null), 900);
    return () => clearTimeout(timeout);
  }, [lockerGainEvent]);

  const tokenCountClasses = [
    'token-count',
    'text-base',
    'font-semibold',
    'leading-none',
    'text-ponpon-navy',
    'sm:text-lg',
  ];
  if (tokenDelta?.kind === 'spend') {
    tokenCountClasses.push('token-count-spending');
  } else if (tokenDelta?.kind === 'gain') {
    tokenCountClasses.push('token-count-gaining');
  }
  const baseLockerCountClasses = [
    'text-base',
    'font-semibold',
    'leading-none',
    'text-ponpon-navy',
    'sm:text-lg',
  ];
  const lockerCountClasses = ['locker-count', ...baseLockerCountClasses];
  if (lockerFlash) {
    lockerCountClasses.push('locker-count-gaining');
  }
  const lockerMaxClasses = ['locker-count-max', ...baseLockerCountClasses];

  const lockerButtonClasses = [
    'hidden',
    'h-9',
    'items-center',
    'gap-2',
    'rounded-full',
    'border',
    'bg-white/90',
    'px-4',
    'text-sm',
    'font-semibold',
    'text-ponpon-navy',
    'shadow-sm',
    'transition',
    'sm:flex',
  ];

  if (lockerActive) {
    lockerButtonClasses.push('border-ponpon-coral/60', 'text-ponpon-coral');
  } else {
    lockerButtonClasses.push('border-white/50', 'hover:border-ponpon-coral/50', 'hover:text-ponpon-coral');
  }

  if (!canOpenLocker) {
    lockerButtonClasses.push('cursor-not-allowed', 'opacity-60', 'hover:border-white/50', 'hover:text-ponpon-navy');
  }

  return (
    <header className="fixed inset-x-0 top-0 z-[60] border-b border-white/40 bg-white/65 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center gap-3">
          {/* CHANGED: Wrap logo with Link to navigate back to homepage */}
          <Link to="/" aria-label="Go to homepage" className="flex items-center gap-2">
            <img
              src="/assets/ponpon-logo.svg"
              alt="PonPon! logo"
              className="h-10 w-auto sm:h-12"
            />
            <span className="sr-only">PonPon!</span>
          </Link>
          <div className="hidden flex-col text-sm font-medium text-ponpon-navy/70 sm:flex">
            <span>A New Way to Experience Gachapon</span>
          </div>
        </div>

        {user ? (
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTokenPanel(true)}
                className="flex h-9 items-center gap-2 rounded-full border border-white/50 bg-white/90 px-4 text-sm font-semibold text-ponpon-navy shadow-sm transition hover:border-ponpon-coral/50 hover:text-ponpon-coral"
              >
                <TokenIcon className="w-5 h-5" alt="Tokens" />
                <span className="token-count-wrapper">
                  <span
                    key={tokenDelta?.id || 'token-count'}
                    className={tokenCountClasses.join(' ')}
                  >
                    {displayTokens}
                  </span>
                  {tokenDelta ? (
                    <span
                      key={`delta-${tokenDelta.id}`}
                      className={`token-count-delta ${tokenDelta.kind === 'gain' ? 'gain' : 'spend'}`}
                      aria-hidden="true"
                    >
                      {tokenDelta.kind === 'gain' ? `+${tokenDelta.amount}` : `-${tokenDelta.amount}`}
                    </span>
                  ) : null}
                </span>
                <span className="hidden text-xs font-medium leading-none text-ponpon-navy/70 sm:inline">
                  Tokens
                </span>
              </button>
              {showTokenPanel ? (
                <div
                  ref={tokenPanelRef}
                  className="absolute left-1/2 top-[calc(100%+1.25rem)] sm:top-[calc(100%+1.75rem)] z-20 -translate-x-1/2"
                >
                  <TokenPurchasePanel onClose={handlePanelClose} />
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={canOpenLocker ? handleLockerClick : undefined}
              disabled={!canOpenLocker || lockerActive}
              className={lockerButtonClasses.join(' ')}
            >
              <img
                src="/assets/locker-icon.png"
                alt="Locker"
                className="w-5 h-5 object-contain flex-shrink-0"
                aria-hidden="true"
              />
              <span className="flex items-end gap-1">
                <span className="locker-count-wrapper">
                  <span
                    key={lockerFlash?.id || 'locker-count'}
                    className={lockerCountClasses.join(' ')}
                  >
                    {lockerCount}
                  </span>
                  {lockerFlash ? (
                    <span
                      key={`locker-delta-${lockerFlash.id}`}
                      className="locker-count-delta"
                      aria-hidden="true"
                    >
                      +{lockerFlash.amount}
                    </span>
                  ) : null}
                </span>
                <span className="text-sm font-semibold leading-none text-ponpon-navy/50 sm:text-base">
                  /
                </span>
                <span className={lockerMaxClasses.join(' ')}>{maxLockerSlots}</span>
              </span>
            </button>
            <ProfileDropdown
              displayName={displayName}
              onLogout={handleLogout}
              onOpenChange={handleProfileMenuOpenChange}
              shouldClose={showTokenPanel}
            />
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-white/60 bg-white/80 px-4 py-2 text-xs font-medium text-ponpon-navy shadow-sm sm:text-sm">
              Create an account to sync your spins &amp; shipments.
            </div>
            {typeof requireLogin === 'function' ? (
              <button
                type="button"
                onClick={() => requireLogin()}
                className="rounded-full bg-ponpon-coral px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60 sm:text-sm"
              >
                Join / Sign in
              </button>
            ) : null}
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
